import { Link } from "react-router-dom";
import { SeoLink } from "@/components/seo/SeoLink";
import { MapPin, Sparkles, ChevronRight, Star } from "lucide-react";
import type { Listing } from "@/lib/types";
import { useNearbyListings } from "@/hooks/use-listings";
import { getStateByNameOrAbbr } from "@/data/states";
import { getTreatmentByTag } from "@/data/treatments";
import { matchListingToCity } from "@/data/cities";

/**
 * SEO content sections rendered on every spa profile page.
 *
 * Purpose: give Google enough unique, substantive content per spa to (1) index the
 * page and (2) distinguish it from every other spa in the directory. Without this,
 * spa pages were being flagged as "Crawled — currently not indexed" and "Alternate
 * page with proper canonical" in Google Search Console.
 *
 * All content is derived from the listing record itself, so every page is unique.
 */
export default function SpaContentSections({ listing }: { listing: Listing }) {
  const stateData = getStateByNameOrAbbr(listing.state);
  const locationLine = [listing.city, listing.state].filter(Boolean).join(", ");
  const tags = listing.tags || [];
  const verified = listing.tier !== "free";
  const hasRating = listing.rating > 0 && listing.review_count > 0;
  const ratingDisplay = hasRating ? listing.rating.toFixed(1) : null;

  const treatmentLinks = tags
    .map((tag) => {
      const t = getTreatmentByTag(tag);
      return t ? { name: t.name, slug: t.slug, tag } : null;
    })
    .filter(Boolean) as { name: string; slug: string; tag: string }[];

  const otherTags = tags.filter((t) => !treatmentLinks.find((l) => l.tag === t));

  // ── Overview paragraph (auto-composed, unique per spa) ─────────────
  const overviewParts: string[] = [];
  overviewParts.push(
    `${listing.name} is a ${verified ? "verified" : "listed"} medical spa located ${
      listing.address ? `at ${listing.address}` : ""
    }${locationLine ? ` in ${locationLine}` : ""}${listing.zip ? ` ${listing.zip}` : ""}.`,
  );
  if (treatmentLinks.length || otherTags.length) {
    const n = treatmentLinks.length + otherTags.length;
    overviewParts.push(
      `The provider offers ${n} aesthetic and wellness ${n === 1 ? "service" : "services"}, including ${[
        ...treatmentLinks.slice(0, 4).map((t) => t.name),
        ...otherTags.slice(0, 4 - Math.min(4, treatmentLinks.length)),
      ]
        .slice(0, 4)
        .join(", ")}${n > 4 ? ", and more" : ""}.`,
    );
  }
  if (hasRating) {
    overviewParts.push(
      `Clients have given ${listing.name} an average rating of ${ratingDisplay} out of 5 based on ${listing.review_count} review${listing.review_count === 1 ? "" : "s"}.`,
    );
  }
  if (listing.phone || listing.website) {
    overviewParts.push(
      `You can ${listing.phone ? `call ${listing.phone}` : ""}${listing.phone && listing.website ? " or " : ""}${
        listing.website ? "visit the spa's website" : ""
      } to book a consultation or learn more about pricing and availability.`,
    );
  }
  const overviewText = overviewParts.join(" ");

  // ── Why-choose composed bullets (unique per spa) ───────────────────
  const whyChoose: string[] = [];
  if (verified) {
    whyChoose.push(
      `Verified listing on MedSpa Directory Pro — ${listing.name} has been confirmed as an active, operating medical spa.`,
    );
  }
  if (listing.is_featured) {
    whyChoose.push(
      `Featured provider in ${stateData?.name || listing.state || "the directory"}, highlighted for quality of service and patient experience.`,
    );
  }
  if (treatmentLinks.length >= 5) {
    whyChoose.push(
      `Full-service menu with ${treatmentLinks.length} different aesthetic treatments under one roof — convenient for patients combining procedures.`,
    );
  }
  if (hasRating && listing.rating >= 4.5) {
    whyChoose.push(
      `Highly rated by patients (${ratingDisplay}/5 from ${listing.review_count} review${
        listing.review_count === 1 ? "" : "s"
      }), suggesting consistent client satisfaction.`,
    );
  }
  if (listing.gallery && listing.gallery.length >= 3) {
    whyChoose.push(
      `Photo gallery of the clinic and treatment rooms is available so you can preview the space before your first visit.`,
    );
  }
  if (listing.hours && listing.hours.length >= 5) {
    whyChoose.push(
      `Open multiple days per week — see the business hours panel for current scheduling.`,
    );
  }
  if (listing.social_links?.instagram || listing.social_links?.facebook) {
    whyChoose.push(
      `Active on social media — follow on ${[
        listing.social_links.instagram && "Instagram",
        listing.social_links.facebook && "Facebook",
      ]
        .filter(Boolean)
        .join(" and ")} for before/after photos and treatment updates.`,
    );
  }

  // ── Auto-generated FAQ (unique per spa, used both visually + for FAQ schema) ──
  // The same FAQ array is injected as JSON-LD in SpaProfile.
  const faqs: { question: string; answer: string }[] = [];
  if (locationLine || listing.address) {
    faqs.push({
      question: `Where is ${listing.name} located?`,
      answer: `${listing.name} is located ${
        listing.address ? `at ${listing.address}, ` : "in "
      }${locationLine}${listing.zip ? ` ${listing.zip}` : ""}.`,
    });
  }
  if (listing.phone) {
    faqs.push({
      question: `What is ${listing.name}'s phone number?`,
      answer: `You can reach ${listing.name} by phone at ${listing.phone}.`,
    });
  }
  if (treatmentLinks.length || otherTags.length) {
    const treatments = [...treatmentLinks.map((t) => t.name), ...otherTags].slice(0, 8).join(", ");
    faqs.push({
      question: `What treatments does ${listing.name} offer?`,
      answer: `${listing.name} offers ${treatments}${
        treatmentLinks.length + otherTags.length > 8 ? ", along with other aesthetic and wellness services" : ""
      }.`,
    });
  }
  if (listing.hours && listing.hours.length > 0) {
    const openDays = listing.hours
      .filter((h) => h.open && h.open.toLowerCase() !== "closed")
      .map((h) => h.day);
    if (openDays.length > 0) {
      faqs.push({
        question: `What days is ${listing.name} open?`,
        answer: `${listing.name} is open on ${openDays.join(", ")}. See the business hours panel for exact opening and closing times.`,
      });
    }
  }
  if (hasRating) {
    faqs.push({
      question: `How is ${listing.name} rated by patients?`,
      answer: `${listing.name} has an average rating of ${ratingDisplay} out of 5 stars based on ${listing.review_count} patient review${
        listing.review_count === 1 ? "" : "s"
      }.`,
    });
  }
  if (listing.website || listing.phone) {
    faqs.push({
      question: `How can I book an appointment at ${listing.name}?`,
      answer: `To book, ${
        listing.phone ? `call ${listing.phone}` : ""
      }${listing.phone && listing.website ? " or " : ""}${
        listing.website ? "visit the spa's website using the link in the contact info panel" : ""
      }. Many medical spas require a consultation before your first treatment.`,
    });
  }

  // ── Nearby listings for internal linking ──
  const { data: nearby } = useNearbyListings({
    city: listing.city,
    state: listing.state,
    excludeSlug: listing.slug,
    limit: 6,
  });

  return (
    <>
      {/* Overview */}
      <section className="p-5 lg:p-6 rounded-2xl border border-border bg-card">
        <h2 className="font-display text-xl font-semibold text-foreground mb-3">
          About {listing.name}
        </h2>
        <p className="text-muted-foreground leading-relaxed text-[15px]">
          {overviewText}
        </p>
      </section>

      {/* Treatments offered — internal links to /treatments/<slug> */}
      {(treatmentLinks.length > 0 || otherTags.length > 0) && (
        <section className="p-5 lg:p-6 rounded-2xl border border-border bg-card">
          <h2 className="font-display text-xl font-semibold text-foreground mb-2">
            Treatments offered at {listing.name}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {(() => {
              const matchedCity = matchListingToCity(listing.city, listing.state);
              return matchedCity
                ? `Browse other ${matchedCity.name} providers for each of these services, or learn more about what to expect from the treatment.`
                : `Browse providers in ${stateData?.name || listing.state || "the U.S."} for each of these services, or learn more about what to expect from the treatment.`;
            })()}
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {treatmentLinks.map((t) => {
              const matchedCity = matchListingToCity(listing.city, listing.state);
              const href = matchedCity
                ? `/browse/${matchedCity.stateSlug}/${matchedCity.slug}/${t.slug}`
                : `/treatments/${t.slug}`;
              return (
                <SeoLink
                  key={t.slug}
                  to={href}
                  className="group flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border bg-background hover:border-primary/40 hover:bg-primary/5 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm text-foreground">
                    <Sparkles className="w-4 h-4 text-primary shrink-0" />
                    {t.name}
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
                </SeoLink>
              );
            })}
            {otherTags.map((tag) => (
              <div
                key={tag}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground"
              >
                <Sparkles className="w-4 h-4 text-muted-foreground shrink-0" />
                {tag}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Why choose */}
      {whyChoose.length > 0 && (
        <section className="p-5 lg:p-6 rounded-2xl border border-border bg-card">
          <h2 className="font-display text-xl font-semibold text-foreground mb-4">
            Why patients choose {listing.name}
          </h2>
          <ul className="space-y-3">
            {whyChoose.map((point, i) => (
              <li key={i} className="flex items-start gap-3 text-[15px] text-muted-foreground">
                <Star className="w-4 h-4 text-primary shrink-0 mt-1" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Location context */}
      {stateData && (
        <section className="p-5 lg:p-6 rounded-2xl border border-border bg-card">
          <h2 className="font-display text-xl font-semibold text-foreground mb-3">
            Medical spas in {stateData.name}
          </h2>
          <p className="text-muted-foreground leading-relaxed text-[15px] mb-4">
            {stateData.description}
          </p>
          <Link
            to={`/browse/${stateData.slug}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-80"
          >
            <MapPin className="w-4 h-4" />
            See all MedSpas in {stateData.name}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="p-5 lg:p-6 rounded-2xl border border-border bg-card">
          <h2 className="font-display text-xl font-semibold text-foreground mb-4">
            Frequently asked questions about {listing.name}
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="pb-4 border-b border-border last:border-0 last:pb-0">
                <h3 className="font-semibold text-foreground mb-1.5 text-[15px]">
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-[14px] leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Nearby spas — internal linking layer */}
      {nearby && nearby.length > 0 && (
        <section className="p-5 lg:p-6 rounded-2xl border border-border bg-card">
          <h2 className="font-display text-xl font-semibold text-foreground mb-1">
            Other MedSpas {listing.city ? `near ${listing.city}` : `in ${stateData?.name || listing.state}`}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Compare with other top-rated providers in the area.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {nearby.map((n) => (
              <Link
                key={n.slug}
                to={`/spa/${n.slug}`}
                className="group flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border bg-background hover:border-primary/40 hover:bg-primary/5 transition-colors"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground truncate">
                    {n.name}
                  </span>
                  {(n.city || n.state) && (
                    <span className="block text-xs text-muted-foreground truncate">
                      {[n.city, n.state].filter(Boolean).join(", ")}
                    </span>
                  )}
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
              </Link>
            ))}
          </div>
          {stateData && (
            <Link
              to={`/browse/${stateData.slug}`}
              className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-primary hover:opacity-80"
            >
              View all MedSpas in {stateData.name}
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </section>
      )}
    </>
  );
}

/**
 * Re-export the FAQ generator so SpaProfile can build matching FAQPage JSON-LD
 * from the exact same question/answer pairs that render in the UI.
 */
export function buildSpaFaqs(listing: Listing): { question: string; answer: string }[] {
  const locationLine = [listing.city, listing.state].filter(Boolean).join(", ");
  const tags = listing.tags || [];
  const hasRating = listing.rating > 0 && listing.review_count > 0;
  const ratingDisplay = hasRating ? listing.rating.toFixed(1) : null;

  const treatmentNames = tags
    .map((tag) => {
      const t = getTreatmentByTag(tag);
      return t ? t.name : tag;
    })
    .slice(0, 8);

  const faqs: { question: string; answer: string }[] = [];

  if (locationLine || listing.address) {
    faqs.push({
      question: `Where is ${listing.name} located?`,
      answer: `${listing.name} is located ${
        listing.address ? `at ${listing.address}, ` : "in "
      }${locationLine}${listing.zip ? ` ${listing.zip}` : ""}.`,
    });
  }
  if (listing.phone) {
    faqs.push({
      question: `What is ${listing.name}'s phone number?`,
      answer: `You can reach ${listing.name} by phone at ${listing.phone}.`,
    });
  }
  if (treatmentNames.length) {
    faqs.push({
      question: `What treatments does ${listing.name} offer?`,
      answer: `${listing.name} offers ${treatmentNames.join(", ")}${
        tags.length > 8 ? ", along with other aesthetic and wellness services" : ""
      }.`,
    });
  }
  if (listing.hours && listing.hours.length > 0) {
    const openDays = listing.hours
      .filter((h) => h.open && h.open.toLowerCase() !== "closed")
      .map((h) => h.day);
    if (openDays.length > 0) {
      faqs.push({
        question: `What days is ${listing.name} open?`,
        answer: `${listing.name} is open on ${openDays.join(", ")}. See the business hours panel for exact opening and closing times.`,
      });
    }
  }
  if (hasRating) {
    faqs.push({
      question: `How is ${listing.name} rated by patients?`,
      answer: `${listing.name} has an average rating of ${ratingDisplay} out of 5 stars based on ${listing.review_count} patient review${
        listing.review_count === 1 ? "" : "s"
      }.`,
    });
  }
  if (listing.website || listing.phone) {
    faqs.push({
      question: `How can I book an appointment at ${listing.name}?`,
      answer: `To book, ${
        listing.phone ? `call ${listing.phone}` : ""
      }${listing.phone && listing.website ? " or " : ""}${
        listing.website ? "visit the spa's website using the link in the contact info panel" : ""
      }. Many medical spas require a consultation before your first treatment.`,
    });
  }

  return faqs;
}
